import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Paper,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Alert,
  Snackbar,
  Chip,
  InputAdornment,
  MenuItem,
  Grid,
  Card,
  CardContent,
  CardActionArea
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Add, Edit, Delete, Search } from '@mui/icons-material';
import { companyAPI, jobAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';

const ENTITY_TYPES = [
  'IT',
  'Logistics'
];

const ENTITY_INFO = {
  'IT': { color: '#8B1538', icon: '💻', description: 'Information Technology services and solutions' },
  'Logistics': { color: '#B8A369', icon: '🚚', description: 'Supply chain and transportation' }
};

const Companies = () => {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentCompany, setCurrentCompany] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [entityFilter, setEntityFilter] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [formData, setFormData] = useState({
    name: '',
    entity: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    website: '',
    description: ''
  });
  const { isAdmin } = useAuth();

  useEffect(() => {
    fetchCompanies();
    fetchJobs();
  }, [searchTerm, entityFilter]);

  const fetchCompanies = async () => {
    setLoading(true);
    try {
      const params = {};
      if (searchTerm) params.search = searchTerm;
      if (entityFilter) params.entity = entityFilter;
      
      const response = await companyAPI.getAll(params);
      setCompanies(response.data.data);
    } catch (error) {
      showSnackbar('Failed to fetch companies', 'error');
    }
    setLoading(false);
  };

  const fetchJobs = async () => {
    try {
      const response = await jobAPI.getAll({ limit: 1000 });
      setJobs(response.data.data);
    } catch (error) {
      console.error('Failed to fetch jobs');
    }
  };

  const handleOpenDialog = (company = null) => {
    if (company) {
      setEditMode(true);
      setCurrentCompany(company);
      setFormData({
        name: company.name,
        entity: company.entity,
        email: company.email || '',
        phone: company.phone || '',
        address: company.address || '',
        city: company.city || '',
        state: company.state || '',
        zipCode: company.zipCode || '',
        website: company.website || '',
        description: company.description || ''
      });
    } else {
      setEditMode(false);
      setCurrentCompany(null);
      setFormData({
        name: '',
        entity: '',
        email: '',
        phone: '',
        address: '',
        city: '',
        state: '',
        zipCode: '',
        website: '',
        description: ''
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setFormData({
      name: '',
      entity: '',
      email: '',
      phone: '',
      address: '',
      city: '',
      state: '',
      zipCode: '',
      website: '',
      description: ''
    });
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editMode) {
        await companyAPI.update(currentCompany._id, formData);
        showSnackbar('Company updated successfully', 'success');
      } else {
        await companyAPI.create(formData);
        showSnackbar('Company created successfully', 'success');
      }
      handleCloseDialog();
      fetchCompanies();
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Operation failed', 'error');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this company?')) {
      try {
        await companyAPI.delete(id);
        showSnackbar('Company deleted successfully', 'success');
        fetchCompanies();
      } catch (error) {
        showSnackbar(error.response?.data?.message || 'Delete failed', 'error');
      }
    }
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const columns = [
    { field: 'name', headerName: 'Company Name', flex: 1, minWidth: 200 },
    { 
      field: 'entity', 
      headerName: 'Entity Type', 
      flex: 1, 
      minWidth: 150,
      renderCell: (params) => (
        <Chip
          label={params.value}
          color="primary"
          variant="outlined"
          size="small"
        />
      )
    },
    { field: 'email', headerName: 'Email', flex: 1, minWidth: 180 },
    { field: 'phone', headerName: 'Phone', flex: 1, minWidth: 130 },
    { field: 'city', headerName: 'City', flex: 1, minWidth: 120 },
    { field: 'state', headerName: 'State', flex: 1, minWidth: 100 },
    {
      field: 'isActive',
      headerName: 'Status',
      width: 100,
      renderCell: (params) => (
        <Chip
          label={params.value ? 'Active' : 'Inactive'}
          color={params.value ? 'success' : 'default'}
          size="small"
        />
      )
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      sortable: false,
      renderCell: (params) => (
        <Box onClick={(e) => e.stopPropagation()}>
          {isAdmin && (
            <>
              <IconButton
                size="small"
                color="primary"
                onClick={(e) => {
                  e.stopPropagation();
                  handleOpenDialog(params.row);
                }}
              >
                <Edit />
              </IconButton>
              <IconButton
                size="small"
                color="error"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDelete(params.row._id);
                }}
              >
                <Delete />
              </IconButton>
            </>
          )}
        </Box>
      )
    }
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4">Companies & Entities</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Browse by entity or select a specific company
          </Typography>
        </Box>
        {isAdmin && (
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpenDialog()}
          >
            Add Company
          </Button>
        )}
      </Box>

      {/* Entity Cards Section */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>Browse by Entity</Typography>
        <Grid container spacing={2}>
          {ENTITY_TYPES.map((entity) => {
            const info = ENTITY_INFO[entity];
            const entityCompanies = companies.filter(c => c.entity === entity);
            const entityJobs = jobs.filter(j => j.entity === entity && !j.company);
            return (
              <Grid item xs={12} sm={6} md={4} lg={2.4} key={entity}>
                <Card 
                  sx={{ 
                    height: '100%',
                    transition: 'all 0.3s',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 4
                    }
                  }}
                >
                  <CardActionArea 
                    onClick={() => navigate(`/entities/${entity}`)}
                    sx={{ height: '100%', p: 2 }}
                  >
                    <CardContent sx={{ p: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <Typography variant="h3" sx={{ mr: 1 }}>{info.icon}</Typography>
                        <Box sx={{ flexGrow: 1 }}>
                          <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            {entity}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                            <Chip 
                              label={`${entityCompanies.length} companies`} 
                              size="small" 
                              sx={{ 
                                backgroundColor: info.color, 
                                color: 'white',
                                fontWeight: 500
                              }} 
                            />
                            <Chip 
                              label={`${entityJobs.length} jobs`} 
                              size="small" 
                              sx={{ 
                                backgroundColor: info.color, 
                                color: 'white',
                                fontWeight: 500,
                                opacity: 0.8
                              }} 
                            />
                          </Box>
                        </Box>
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        {info.description}
                      </Typography>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </Box>

      {/* Companies Section */}
      <Typography variant="h6" sx={{ mb: 2 }}>All Companies</Typography>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={8}>
            <TextField
              fullWidth
              placeholder="Search companies..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                )
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              select
              fullWidth
              label="Filter by Entity"
              value={entityFilter}
              onChange={(e) => setEntityFilter(e.target.value)}
            >
              <MenuItem value="">All Entities</MenuItem>
              {ENTITY_TYPES.map((entity) => (
                <MenuItem key={entity} value={entity}>
                  {entity}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ height: 600, width: '100%', mt: 2 }}>
        <DataGrid
          rows={companies}
          columns={columns}
          pageSize={10}
          rowsPerPageOptions={[10, 25, 50]}
          loading={loading}
          getRowId={(row) => row._id}
          onRowClick={(params) => navigate(`/companies/${params.row._id}`)}
          sx={{
            '& .MuiDataGrid-row': {
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: 'action.hover'
              }
            }
          }}
        />
      </Paper>

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>{editMode ? 'Edit Company' : 'Add New Company'}</DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  autoFocus
                  margin="dense"
                  name="name"
                  label="Company Name"
                  type="text"
                  fullWidth
                  required
                  value={formData.name}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  select
                  margin="dense"
                  name="entity"
                  label="Entity Type"
                  fullWidth
                  required
                  value={formData.entity}
                  onChange={handleChange}
                >
                  {ENTITY_TYPES.map((entity) => (
                    <MenuItem key={entity} value={entity}>
                      {entity}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  margin="dense"
                  name="email"
                  label="Email"
                  type="email"
                  fullWidth
                  value={formData.email}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  margin="dense"
                  name="phone"
                  label="Phone"
                  type="text"
                  fullWidth
                  value={formData.phone}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  margin="dense"
                  name="address"
                  label="Address"
                  type="text"
                  fullWidth
                  value={formData.address}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  margin="dense"
                  name="city"
                  label="City"
                  type="text"
                  fullWidth
                  value={formData.city}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  margin="dense"
                  name="state"
                  label="State"
                  type="text"
                  fullWidth
                  value={formData.state}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  margin="dense"
                  name="zipCode"
                  label="Zip Code"
                  type="text"
                  fullWidth
                  value={formData.zipCode}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  margin="dense"
                  name="website"
                  label="Website"
                  type="url"
                  fullWidth
                  value={formData.website}
                  onChange={handleChange}
                  placeholder="https://example.com"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  margin="dense"
                  name="description"
                  label="Description"
                  type="text"
                  fullWidth
                  multiline
                  rows={3}
                  value={formData.description}
                  onChange={handleChange}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button type="submit" variant="contained">
              {editMode ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Companies;
